import React, { useEffect } from 'react';
import { useQuery } from '@apollo/client';
import moment from 'moment';
import { useRouter } from 'next/router';
import Link from 'nextDir/modules/Router/Link';
import { defineMessages, useIntl } from 'react-intl';

import { generateSlug } from 'src/common/generateSlug';
import { useConfig } from 'src/common/hooks';
import { useCountryData } from 'src/common/hooks/useCountryData';
import { usePageNumberTranslation } from 'src/common/hooks/usePageNumberTranslation';
import { ROUTES } from 'src/common/routes';
import GlintsContainer from 'src/components/GlintsContainer';
import { MetadataRenderer } from 'src/components/MetaDescription/MetadataRenderer';
import { AnchorPagination } from 'src/components/Pagination/AnchorPagination';
import { createUrlSlug } from 'src/modules/Opportunities/utils/createUrlSlug';
import {
  GetActiveJobsArg,
  GetCompaniesWithActiveJobsData,
  queryGetCompaniesWithActiveJobs,
} from 'src/modules/Seo/graphql';

import * as S from './JobsIndexPage.sc';

const getTime = () => moment().format('MMMM, YYYY');
const getMonthNumber = () => moment().format('M');
const getYear = () => moment().format('YYYY');

export const JobsByCompanyNamePage: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const router = useRouter();
  const intl = useIntl();
  const { LANG: language } = useConfig();
  const { countryCode, englishCountryName } = useCountryData();

  const currentPage = Number(router.query.page) || 1;
  const pageTranslation = usePageNumberTranslation(currentPage);

  const { data, error } = useQuery<
    GetCompaniesWithActiveJobsData,
    GetActiveJobsArg
  >(queryGetCompaniesWithActiveJobs, {
    variables: {
      countryCode,
      pageSize: 60,
      currentPage,
    },
  });

  const time = getTime();
  const monthNumber = getMonthNumber();
  const year = getYear();

  useEffect(function redirectIfNeeded() {
    const pageOutOfRange =
      data && currentPage > data.getCompaniesWithActiveJobs.pageInfo.totalPage;
    const positiveTotalCount =
      data?.getCompaniesWithActiveJobs.pageInfo.totalCount > 0;

    if (error) {
      router.replace('/500');
    } else if (positiveTotalCount && pageOutOfRange) {
      router.push({ pathname: router.pathname }, undefined, {
        shallow: true,
      });
    }
  });

  return (
    <>
      <MetadataRenderer
        title={intl.formatMessage(messages.title, {
          country: englishCountryName,
          pageTranslation,
          monthNumber,
          year,
        })}
        description={intl.formatMessage(messages.description, {
          country: englishCountryName,
          time,
        })}
        glintsTrackingName="explore"
      />
      <GlintsContainer>
        <S.Header>
          {intl.formatMessage(messages.h1, { country: englishCountryName })}
        </S.Header>
        {data && (
          <>
            <S.ListContainer>
              <S.LinkList>
                {data.getCompaniesWithActiveJobs.companies.map(
                  ({ Company }) => {
                    const companyNameSlug = generateSlug(Company.name);
                    const urlSlug = createUrlSlug({
                      slug: companyNameSlug,
                      countryCode,
                      language,
                    });
                    return (
                      <S.LinkItem key={Company.id}>
                        <Link
                          href={`/${ROUTES.opportunitiesJobsExplore}`}
                          as={`/company/${urlSlug}/${Company.id}`}
                        >
                          {Company.name}
                        </Link>
                      </S.LinkItem>
                    );
                  }
                )}
              </S.LinkList>
            </S.ListContainer>
            {data.getCompaniesWithActiveJobs.pageInfo.totalPage > 1 && (
              <S.PaginationContainer>
                <AnchorPagination
                  totalPage={data.getCompaniesWithActiveJobs.pageInfo.totalPage}
                  currentPage={currentPage}
                  basePathname="/browse/company"
                />
              </S.PaginationContainer>
            )}
          </>
        )}
      </GlintsContainer>
    </>
  );
};

const messages = defineMessages({
  title: {
    id: 'jobs-by-company-name-page_meta_title',
    defaultMessage:
      'Browse Jobs In {country} by Company {pageTranslation}| Glints',
  },
  description: {
    id: 'jobs-by-company-name-page_meta_description',
    defaultMessage:
      'Discover jobs opportunities in more than 1,000 companies in {country} with Glints: Career Discovery without Limits',
  },
  h1: {
    id: 'jobs-by-company-name-page_header',
    defaultMessage: 'Browse Jobs In {country} by Company',
  },
});
